import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getEnv } from '@/config/env';
import { createPkceRequest } from './pkce';
import { exchangePkceCallbackOnce } from './callbackExchange';
import { AuthExchangeGeneration } from './exchangeGeneration';
import {
  decodeTokenPayload,
  isStoredTokenCurrent,
  parseStoredTokens,
  refreshStoredTokensOrClear,
  resolveStoredTokens,
  storedTokenExpiryMs,
  type StoredTokens,
} from './tokens';
import { authUserFromClaims, normalizeNickname, type AuthUser } from './authUser';
import { OFFLINE_MODE, OFFLINE_PROFILE_ID } from '@/config/offline';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

type AuthContextValue = {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
  getAccessToken: () => Promise<string>;
  setNickname: (nickname: string) => void;
  signIn: () => Promise<void>;
  signOut: () => void;
  endSession: () => void;
};

const TOKEN_KEY = 'filo:auth:tokens';
const NICKNAME_KEY_PREFIX = 'filo:profile:nickname:';
const AuthContext = createContext<AuthContextValue | null>(null);

const applyBrowserNickname = (user: AuthUser): AuthUser => {
  try {
    const stored = localStorage.getItem(`${NICKNAME_KEY_PREFIX}${user.sub}`);
    if (!stored) return user;
    const nickname = normalizeNickname(stored);
    return { ...user, username: nickname, nickname };
  } catch {
    return user;
  }
};

const toUser = (idToken: string): AuthUser => {
  const payload = decodeTokenPayload(idToken);
  return authUserFromClaims(payload);
};

const loadTokens = (key = TOKEN_KEY): StoredTokens | null => {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? parseStoredTokens(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const saveTokens = (tokens: StoredTokens | null, key = TOKEN_KEY) => {
  if (tokens) sessionStorage.setItem(key, JSON.stringify(tokens));
  else sessionStorage.removeItem(key);
};

const requestTokens = async (body: URLSearchParams): Promise<StoredTokens> => {
  const env = getEnv();
  const response = await fetch(`${env.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) throw new Error(`ログインセッションを取得できませんでした (${response.status})`);
  const payload = await response.json() as {
    access_token?: string;
    id_token?: string;
    refresh_token?: string;
    expires_in?: number;
  };
  if (!payload.access_token || !payload.id_token) throw new Error('認証サーバーの応答が不完全です');
  return {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + Math.max(60, payload.expires_in ?? 3600) * 1000,
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  return OFFLINE_MODE ? <LocalProfileProvider>{children}</LocalProfileProvider> : <OnlineAuthProvider>{children}</OnlineAuthProvider>;
}

function LocalProfileProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser>(() => applyBrowserNickname({
    sub: OFFLINE_PROFILE_ID, username: 'ローカルユーザー', nickname: 'ローカルユーザー', email: null, avatarUrl: null,
  }));
  const value: AuthContextValue = {
    status: 'authenticated',
    user,
    error: null,
    getAccessToken: async () => { throw new Error('オフラインモードではクラウド認証を利用できません'); },
    setNickname: (value) => {
      const nickname = normalizeNickname(value);
      localStorage.setItem(`${NICKNAME_KEY_PREFIX}${OFFLINE_PROFILE_ID}`, nickname);
      setUser({ ...user, username: nickname, nickname });
    },
    signIn: async () => { throw new Error('オフラインモードではログインを利用できません'); },
    signOut: () => window.location.reload(),
    endSession: () => window.location.reload(),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AudioCreationAuthProvider({ children }: { children: ReactNode }) {
  return <OnlineAuthProvider tokenKey="filo:audio-auth:tokens">{children}</OnlineAuthProvider>;
}

function OnlineAuthProvider({ children, tokenKey = TOKEN_KEY }: { children: ReactNode; tokenKey?: string }) {
  const exchangeGenerationRef = useRef(new AuthExchangeGeneration());
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tokens, setTokens] = useState<StoredTokens | null>(() => loadTokens(tokenKey));

  const applyTokens = useCallback((next: StoredTokens | null) => {
    if (next && !isStoredTokenCurrent(next)) throw new Error('ログインの有効期限が切れました');
    saveTokens(next, tokenKey);
    setTokens(next);
    if (!next) {
      setUser(null);
      setStatus('unauthenticated');
      return;
    }
    setUser(applyBrowserNickname(toUser(next.idToken)));
    setStatus('authenticated');
    setError(null);
  }, [tokenKey]);

  const requestRefreshTokens = useCallback(async (current: StoredTokens): Promise<StoredTokens> => {
    if (!current.refreshToken) throw new Error('ログインの有効期限が切れました');
    const env = getEnv();
    const next = await requestTokens(new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: env.cognitoClientId,
      refresh_token: current.refreshToken,
    }));
    return { ...next, refreshToken: next.refreshToken ?? current.refreshToken };
  }, []);

  const refresh = useCallback(async (current: StoredTokens): Promise<StoredTokens> => {
    const generation = exchangeGenerationRef.current.capture();
    const merged = await refreshStoredTokensOrClear(current, requestRefreshTokens, () => {
      exchangeGenerationRef.current.applyIfCurrent(generation, () => applyTokens(null));
    });
    if (!exchangeGenerationRef.current.applyIfCurrent(generation, () => applyTokens(merged))) {
      throw new DOMException('Signed out during token refresh', 'AbortError');
    }
    return merged;
  }, [applyTokens, requestRefreshTokens]);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      const generation = exchangeGenerationRef.current.capture();
      try {
        getEnv();
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const callbackState = params.get('state');
        const callbackError = params.get('error_description') || params.get('error');
        if (callbackError) throw new Error(callbackError);
        if (code && callbackState) {
          const env = getEnv();
          const next = await exchangePkceCallbackOnce(code, callbackState, (verifier) => requestTokens(new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: env.cognitoClientId,
            code,
            redirect_uri: env.redirectUri,
            code_verifier: verifier,
          })));
          window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
          if (active) exchangeGenerationRef.current.applyIfCurrent(generation, () => applyTokens(next));
          return;
        }
        const existing = loadTokens(tokenKey);
        const restored = await resolveStoredTokens(existing, requestRefreshTokens);
        if (active) exchangeGenerationRef.current.applyIfCurrent(generation, () => applyTokens(restored));
      } catch (caught) {
        if (!active) return;
        setError(caught instanceof Error ? caught.message : String(caught));
        setStatus('error');
      }
    };
    void restore();
    return () => { active = false; };
  }, [applyTokens, requestRefreshTokens, tokenKey]);

  const signIn = useCallback(async () => {
    const env = getEnv();
    const request = await createPkceRequest();
    const params = new URLSearchParams({
      client_id: env.cognitoClientId,
      response_type: 'code',
      scope: 'openid email profile filo/operator',
      redirect_uri: env.redirectUri,
      state: request.state,
      code_challenge: request.challenge,
      code_challenge_method: 'S256',
      lang: 'ja',
    });
    window.location.assign(`${env.cognitoDomain}/oauth2/authorize?${params}`);
  }, []);

  const getAccessToken = useCallback(async () => {
    const current = tokens ?? loadTokens(tokenKey);
    if (!current) throw new Error('ログインが必要です');
    if (storedTokenExpiryMs(current) - Date.now() <= 60_000) return (await refresh(current)).accessToken;
    return current.accessToken;
  }, [refresh, tokens, tokenKey]);

  const setNickname = useCallback((value: string) => {
    if (!user) throw new Error('ログインが必要です');
    const nickname = normalizeNickname(value);
    localStorage.setItem(`${NICKNAME_KEY_PREFIX}${user.sub}`, nickname);
    setUser({ ...user, username: nickname, nickname });
  }, [user]);

  const endSession = useCallback(() => {
    exchangeGenerationRef.current.invalidate();
    applyTokens(null);
  }, [applyTokens]);

  const signOut = useCallback(() => {
    const env = getEnv();
    endSession();
    const params = new URLSearchParams({ client_id: env.cognitoClientId, logout_uri: env.logoutUri });
    window.location.assign(`${env.cognitoDomain}/logout?${params}`);
  }, [endSession]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    user,
    error,
    getAccessToken,
    setNickname,
    signIn,
    signOut,
    endSession,
  }), [endSession, error, getAccessToken, setNickname, signIn, signOut, status, user]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider がありません');
  return value;
};
